<script setup>
import { defineEmits, defineProps, computed } from "vue";
import { useI18n } from "vue-i18n";
import { get } from "lodash";
import { useViewportSize } from '@dutypay/front-end-design-system/src/composables';

const { isMobile } = useViewportSize();
const { t } = useI18n();
const emit = defineEmits(['update', 'go-to']);
const props = defineProps({
  modelValue: { type: Object, required: true },
});

const hasAuthenticatorSetup = computed(() => {
  return get(props.modelValue, "mfaModes.authenticator", false);
});
const hasSMSSetup = computed(() => {
  return get(props.modelValue, "mfaModes.sms", false);
});
const hasNoMFA = computed(() => {
  return !hasAuthenticatorSetup.value && !hasSMSSetup.value;
});

function goTo(component) {
  emit("go-to", component);
}
</script>
<script>
export default {
  name: "MFAChoice"
};
</script>

<template>
  <div>
    <h2>{{ t("sso.pages.LoginStages.MFAChoice.MFAChoice.title") }}</h2>
    <p>{{ t("sso.pages.LoginStages.MFAChoice.explanationMFAChoice.label") }}</p>

    <div
      v-if="hasNoMFA || hasAuthenticatorSetup"
      class="button-area dp-flex row align-items__center"
      tabindex="0"
      @click="hasAuthenticatorSetup ? goTo('AuthenticatorOTPForm') : goTo('AuthenticatorSetupForm')"
      @keypress="
        hasAuthenticatorSetup ? goTo('AuthenticatorOTPForm') : goTo('AuthenticatorSetupForm')
      "
    >
      <div v-if="!isMobile" class="icon">
        <DpIcon icon="mobile" :margin="false" />
      </div>
      <div class="flex-basis-1 button-content">
        <div class="headline dp-flex row">
          <DpIcon v-if="isMobile" icon="mobile" />{{ t("sso.pages.LoginStages.MFAChoice.authenticatorMFA.label") }}
        </div>
        <div v-if="hasAuthenticatorSetup" class="dp-color__font__success dp-flex row">
          <span><DpIcon icon="check" /></span>
          <span>{{ t("sso.pages.LoginStages.MFAChoice.feedforward.authenticatorMFA.success.message") }}</span>
        </div>
        <div>{{ t("sso.pages.LoginStages.MFAChoice.authenticatorMFA.description.label") }}</div>
      </div>
    </div>

    <div
      v-if="hasNoMFA || hasSMSSetup"
      class="button-area dp-flex row align-items__center"
      tabindex="0"
      @click="hasSMSSetup ? goTo('SMSOTPForm') : goTo('SMSSetupForm')"
      @keypress="hasSMSSetup ? goTo('SMSOTPForm') : goTo('SMSSetupForm')"
    >
      <div v-if="!isMobile" class="icon">
        <DpIcon icon="mobile" :margin="false" />
      </div>
      <div class="flex-basis-1 button-content">
        <div class="headline dp-flex row">
          <DpIcon v-if="isMobile" icon="mobile" />{{ t("sso.pages.LoginStages.MFAChoice.SMSMFA.label") }}
        </div>
        <div v-if="hasSMSSetup" class="dp-color__font__success dp-flex row">
          <span><DpIcon icon="check" /></span>
          <span>{{ t("sso.pages.LoginStages.MFAChoice.feedforward.SMSMFA.success.message") }}</span>
        </div>
        <div>{{ t("sso.pages.LoginStages.MFAChoice.SMSMFA.description.label") }}</div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.button-area {
  border-radius: 6px;
  padding: 0.4em 0.8em;
  margin: 2rem 0;

  &:hover,
  &:focus {
    background-color: #515151;
    cursor: pointer;
  }

  .button-content {
    padding: 0.4em 1.2em 0.8em 1.2em;

    .headline {
      font-size: 1.8em;
    }

    div {
      padding: 0.5rem 0;
    }
  }

  .icon {
    margin: 0.4em 0.2em;
    font-size: 3em;
  }
}
.dp-color__font__success {
  color: var(--use-theme__event__success__font-color);
}

.skip {
  text-align: center;
  margin: 2rem 0;
}

@media only screen and (max-width: 767px) {
  //xs-only
  .button-area {
    padding: 0.2em 1.2em;
    background-color: #515151;

    .button-content {
      padding: 0.4em 0 0.8em 0;
    }
  }
}
</style>

<i18n>
{
  "ar-AE": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "تم التنشيط"
        }
      },
      "label": "رسالة قصيرة",
      "description": {
        "label": "تلقي رمز التحقق عبر رسالة نصية قصيرة"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "تم التنشيط"
        }
      },
      "description": {
        "label": "احصل على رمز التحقق عبر تطبيق التحقق"
      },
      "label": "تطبيق المصادقة"
    },
    "explanationMFAChoice": {
      "label": "قم بزيادة الأمان باستخدام المصادقة متعددة العوامل عند تسجيل الدخول."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "تم التفعيل"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "تم التفعيل"
        }
      }
    },
    "MFAChoice": {
      "title": "مصادقة متعددة العوامل"
    }
  },
  "ar-DZ": {
    "SMSMFA": {
      "feedforward": {
        "success": {}
      },
      "description": {
        "label": "استلام رمز التوثيق عبر الرسائل القصيرة"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {}
      },
      "description": {
        "label": "احصل على رمز التحقق عبر تطبيق التحقق"
      },
      "label": "تطبيق المصادقة"
    },
    "explanationMFAChoice": {},
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "تم التفعيل"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "تم التفعيل"
        }
      }
    },
    "MFAChoice": {}
  },
  "ar-EG": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "تم التنشيط"
        }
      },
      "label": "رسالة قصيرة",
      "description": {
        "label": "تلقي رمز التحقق عبر رسالة نصية قصيرة"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "تم التنشيط"
        }
      },
      "description": {
        "label": "احصل على رمز التحقق عبر تطبيق التحقق"
      },
      "label": "تطبيق المصادقة"
    },
    "explanationMFAChoice": {
      "label": "قم بزيادة الأمان باستخدام المصادقة متعددة العوامل عند تسجيل الدخول."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "تم التفعيل"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "تم التفعيل"
        }
      }
    },
    "MFAChoice": {
      "title": "مصادقة متعددة العوامل"
    }
  },
  "ar-LB": {
    "SMSMFA": {
      "feedforward": {
        "success": {}
      },
      "description": {
        "label": "تلقي رمز التحقق عبر رسالة نصية قصيرة"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {}
      },
      "description": {
        "label": "احصل على رمز التحقق عبر تطبيق التحقق"
      },
      "label": "تطبيق التوثيق"
    },
    "explanationMFAChoice": {},
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "تم التفعيل"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "تم التفعيل"
        }
      }
    },
    "MFAChoice": {}
  },
  "ar-MA": {
    "SMSMFA": {
      "feedforward": {
        "success": {}
      },
      "description": {
        "label": "استلام رمز التوثيق عبر الرسائل القصيرة"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {}
      },
      "description": {
        "label": "احصل على رمز التحقق عبر تطبيق التحقق"
      },
      "label": "تطبيق التوثيق"
    },
    "explanationMFAChoice": {},
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "تم التفعيل"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "تم التفعيل"
        }
      }
    },
    "MFAChoice": {}
  },
  "ar-SA": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "تم التنشيط"
        }
      },
      "label": "رسالة قصيرة",
      "description": {
        "label": "تلقي رمز التحقق عبر رسالة نصية قصيرة"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "تم التنشيط"
        }
      },
      "description": {
        "label": "احصل على رمز التحقق عبر تطبيق التحقق"
      },
      "label": "تطبيق المصادقة"
    },
    "explanationMFAChoice": {
      "label": "قم بزيادة الأمان باستخدام المصادقة متعددة العوامل عند تسجيل الدخول."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "تم التنشيط"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "تم التنشيط"
        }
      }
    },
    "MFAChoice": {
      "title": "مصادقة متعددة العوامل"
    }
  },
  "ar-SY": {
    "SMSMFA": {
      "feedforward": {
        "success": {}
      },
      "description": {
        "label": "تلقي رمز التحقق عبر رسالة نصية قصيرة"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {}
      },
      "description": {
        "label": "احصل على رمز التحقق عبر تطبيق التحقق"
      },
      "label": "تطبيق المصادقة"
    },
    "explanationMFAChoice": {},
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "تم التفعيل"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "تم التفعيل"
        }
      }
    },
    "MFAChoice": {}
  },
  "ar-TN": {
    "SMSMFA": {
      "feedforward": {
        "success": {}
      },
      "description": {
        "label": "استلام رمز التوثيق عبر الرسائل القصيرة"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {}
      },
      "description": {
        "label": "احصل على رمز التحقق عبر تطبيق التحقق"
      },
      "label": "تطبيق التوثيق"
    },
    "explanationMFAChoice": {},
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "تم التفعيل"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "تم التفعيل"
        }
      }
    },
    "MFAChoice": {}
  },
  "bg-BG": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Активирано"
        }
      },
      "label": "SMS",
      "description": {
        "label": "Получете код за удостоверяване чрез SMS"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Активирано"
        }
      },
      "description": {
        "label": "Получете код за удостоверяване през приложение за удостоверяване"
      },
      "label": "Приложение за удостоверяване"
    },
    "explanationMFAChoice": {
      "label": "Увеличете защитата с многофакторно удостоверяване при влизане."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Активирано"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Активирано"
        }
      }
    },
    "MFAChoice": {
      "title": "Многофакторно удостоверяване"
    }
  },
  "bs-BA": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Aktivirano"
        }
      },
      "label": "SMS",
      "description": {
        "label": "Primite autentifikacijski kod putem SMS-a"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Aktivirano"
        }
      },
      "description": {
        "label": "Dobijte autentifikacioni kod putem autentifikacijske aplikacije"
      },
      "label": "Aplikacija za autentifikaciju"
    },
    "explanationMFAChoice": {
      "label": "Povećajte sigurnost pomoću višefaktorske autentifikacije prilikom prijavljivanja."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Aktivirano"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Aktivirano"
        }
      }
    },
    "MFAChoice": {
      "title": "Višefaktorska autentifikacija"
    }
  },
  "cs-CZ": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Aktivováno"
        }
      },
      "label": "SMS",
      "description": {
        "label": "Přijměte ověřovací kód prostřednictvím SMS"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Aktivováno"
        }
      },
      "description": {
        "label": "Získejte ověřovací kód pomocí ověřovací aplikace"
      },
      "label": "Autentizační aplikace"
    },
    "explanationMFAChoice": {
      "label": "Zvyšte zabezpečení pomocí vícefaktorového ověřování při přihlášení."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Aktivováno"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Aktivováno"
        }
      }
    },
    "MFAChoice": {
      "title": "Vícefaktorové ověřování"
    }
  },
  "da-DK": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Aktiveret"
        }
      },
      "label": "SMS",
      "description": {
        "label": "Modtag autentificeringskode via SMS"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Aktiveret"
        }
      },
      "description": {
        "label": "Modtag autentificeringskode via autentificeringsapp"
      },
      "label": "Autentificeringsapp"
    },
    "explanationMFAChoice": {
      "label": "Forøg sikkerheden med multifaktorautentificering, når du logger ind."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Aktiveret"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Aktiveret"
        }
      }
    },
    "MFAChoice": {
      "title": "Multifaktorautentificering"
    }
  },
  "de-DE": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Aktiviert"
        }
      },
      "label": "SMS",
      "description": {
        "label": "Authentifizierungscode per SMS erhalten"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Aktiviert"
        }
      },
      "description": {
        "label": "Authentifizierungscode per Authentifizierungs-App erhalten."
      },
      "label": "Authentifizierungs-App"
    },
    "explanationMFAChoice": {
      "label": "Erhöhe die Sicherheit durch Multi-Faktor-Authentifizierung beim Login."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Aktiviert"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Aktiviert"
        }
      }
    },
    "MFAChoice": {
      "title": "Multi-Faktor-Authentifizierung"
    }
  },
  "de-AT": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Aktiviert"
        }
      },
      "label": "SMS",
      "description": {
        "label": "Authentifizierungscode per SMS erhalten"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Aktiviert"
        }
      },
      "description": {
        "label": "Authentifizierungscode per Authentifizierungs-App erhalten."
      },
      "label": "Authentifizierungs-App"
    },
    "explanationMFAChoice": {
      "label": "Erhöhe die Sicherheit durch Multi-Faktor-Authentifizierung beim Login."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Aktiviert"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Aktiviert"
        }
      }
    },
    "MFAChoice": {
      "title": "Multi-Faktor-Authentifizierung"
    }
  },
  "de-CH": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Aktiviert"
        }
      },
      "label": "SMS",
      "description": {
        "label": "Authentifizierungscode per SMS erhalten"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Aktiviert"
        }
      },
      "description": {
        "label": "Authentifizierungscode per Authentifizierungs-App erhalten."
      },
      "label": "Authentifizierungs-App"
    },
    "explanationMFAChoice": {
      "label": "Erhöhe die Sicherheit durch Multi-Faktor-Authentifizierung beim Login."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Aktiviert"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Aktiviert"
        }
      }
    },
    "MFAChoice": {
      "title": "Multi-Faktor-Authentifizierung"
    }
  },
  "de-LU": {
    "SMSMFA": {
      "feedforward": {
        "success": {}
      },
      "description": {
        "label": "Authentifizierungscode per SMS erhalten"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {}
      },
      "description": {
        "label": "Authentifizierungscode per Authentifizierungs-App erhalten."
      },
      "label": "Authentifizierungs-App"
    },
    "explanationMFAChoice": {},
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Aktiviert"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Aktiviert"
        }
      }
    },
    "MFAChoice": {}
  },
  "el-GR": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Ενεργοποιήθηκε"
        }
      },
      "label": "γραπτό μήνυμα",
      "description": {
        "label": "Λάβετε κωδικό επαλήθευσης μέσω SMS"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Ενεργοποιήθηκε"
        }
      },
      "description": {
        "label": "Λάβετε κώδικα επαλήθευσης μέσω εφαρμογής επαλήθευσης"
      },
      "label": "Εφαρμογή Επαλήθευσης"
    },
    "explanationMFAChoice": {
      "label": "Αυξήστε την ασφάλεια με έλεγχο ταυτότητας πολλών παραγόντων κατά τη σύνδεση."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Ενεργοποιήθηκε"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Ενεργοποιήθηκε"
        }
      }
    },
    "MFAChoice": {
      "title": "Έλεγχος ταυτότητας πολλών παραγόντων"
    }
  },
  "el-CY": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Ενεργοποιήθηκε"
        }
      },
      "label": "γραπτό μήνυμα",
      "description": {
        "label": "Λάβετε κωδικό επαλήθευσης μέσω SMS"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Ενεργοποιήθηκε"
        }
      },
      "description": {
        "label": "Λάβετε κώδικα επαλήθευσης μέσω εφαρμογής επαλήθευσης"
      },
      "label": "Εφαρμογή Επαλήθευσης"
    },
    "explanationMFAChoice": {
      "label": "Αυξήστε την ασφάλεια με έλεγχο ταυτότητας πολλών παραγόντων κατά τη σύνδεση."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Ενεργοποιήθηκε"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Ενεργοποιήθηκε"
        }
      }
    },
    "MFAChoice": {
      "title": "Έλεγχος ταυτότητας πολλών παραγόντων"
    }
  },
  "en-GB": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Activated"
        }
      },
      "label": "SMS",
      "description": {
        "label": "Receive Authentication Code via SMS"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Activated"
        }
      },
      "description": {
        "label": "Receive authentication code via authentication app."
      },
      "label": "Authentication App"
    },
    "explanationMFAChoice": {
      "label": "Increase security with multi-factor authentication for login."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Activated"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Activated"
        }
      }
    },
    "MFAChoice": {
      "title": "Multi-Factor Authentication"
    }
  },
  "en-IE": {
    "SMSMFA": {
      "feedforward": {
        "success": {}
      },
      "description": {
        "label": "Receive Authentication Code via SMS"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {}
      },
      "description": {
        "label": "Receive authentication code via authentication app."
      },
      "label": "Authentication App"
    },
    "explanationMFAChoice": {},
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Activated"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Activated"
        }
      }
    },
    "MFAChoice": {}
  },
  "en-US": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Activated"
        }
      },
      "label": "SMS",
      "description": {
        "label": "Receive Authentication Code via SMS"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Activated"
        }
      },
      "description": {
        "label": "Receive authentication code via authentication app."
      },
      "label": "Authentication App"
    },
    "explanationMFAChoice": {
      "label": "Increase security with multi-factor authentication for login."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Activated"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Activated"
        }
      }
    },
    "MFAChoice": {
      "title": "Multi-Factor Authentication"
    }
  },
  "es-ES": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Activada"
        }
      },
      "label": "SMS",
      "description": {
        "label": "Recibir código de autenticación vía SMS"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Activada"
        }
      },
      "description": {
        "label": "Recibe código de autenticación a través de la aplicación de autenticación"
      },
      "label": "Aplicación de autenticación"
    },
    "explanationMFAChoice": {
      "label": "Aumente la seguridad de su cuenta con la autenticación multifactor para iniciar sesión."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Activada"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Activada"
        }
      }
    },
    "MFAChoice": {
      "title": "Autenticación multifactor"
    }
  },
  "et-EE": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Aktiveeritud"
        }
      },
      "label": "SMS",
      "description": {
        "label": "Saage autentimiskood SMS-i teel"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Aktiveeritud"
        }
      },
      "description": {
        "label": "Saa autentimiskoodi autentimisrakenduse kaudu"
      },
      "label": "Autentimisrakendus"
    },
    "explanationMFAChoice": {
      "label": "Suurendage sisselogimisel turvalisust mitme teguri autentimisega."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Aktiveeritud"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Aktiveeritud"
        }
      }
    },
    "MFAChoice": {
      "title": "Mitmeteguriline autentimine"
    }
  },
  "fi-FI": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Aktivoitu"
        }
      },
      "label": "Tekstiviesti",
      "description": {
        "label": "Vastaanota vahvistuskoodi SMS: n kautta"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Aktivoitu"
        }
      },
      "description": {
        "label": "Saa tunnistuskoodi tunnistussovelluksen kautta"
      },
      "label": "Todennussovellus"
    },
    "explanationMFAChoice": {
      "label": "Paranna tietoturvaa monivaiheisella todennuksella sisäänkirjautumisen yhteydessä."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Aktivoitu"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Aktivoitu"
        }
      }
    },
    "MFAChoice": {
      "title": "Monivaiheinen todennus"
    }
  },
  "fr-BE": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Activé"
        }
      },
      "label": "SMS",
      "description": {
        "label": "Recevez le code d'authentification par SMS"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Activé"
        }
      },
      "description": {
        "label": "Recevez le code d'authentification via l'application d'authentification"
      },
      "label": "Application d'authentification"
    },
    "explanationMFAChoice": {
      "label": "Renforcez la sécurité grâce à l'authentification multifactorielle pour la connexion."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Activé"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Activé"
        }
      }
    },
    "MFAChoice": {
      "title": "Authentification multi-facteurs"
    }
  },
  "fr-CH": {
    "SMSMFA": {
      "feedforward": {
        "success": {}
      },
      "description": {
        "label": "Recevez le code d'authentification par SMS"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {}
      },
      "description": {
        "label": "Recevez le code d'authentification via l'application d'authentification"
      },
      "label": "Application d'authentification"
    },
    "explanationMFAChoice": {},
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Activé"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Activé"
        }
      }
    },
    "MFAChoice": {}
  },
  "fr-FR": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Activé"
        }
      },
      "label": "SMS",
      "description": {
        "label": "Recevez le code d'authentification par SMS"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Activé"
        }
      },
      "description": {
        "label": "Recevez le code d'authentification via l'application d'authentification"
      },
      "label": "Application d'authentification"
    },
    "explanationMFAChoice": {
      "label": "Renforcez la sécurité grâce à l'authentification multifactorielle pour la connexion."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Activé"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Activé"
        }
      }
    },
    "MFAChoice": {
      "title": "Authentification multi-facteurs"
    }
  },
  "fr-LU": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Activé"
        }
      },
      "label": "SMS",
      "description": {
        "label": "Recevez le code d'authentification par SMS"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Activé"
        }
      },
      "description": {
        "label": "Recevez le code d'authentification via l'application d'authentification"
      },
      "label": "Application d'authentification"
    },
    "explanationMFAChoice": {
      "label": "Renforcez la sécurité grâce à l'authentification multifactorielle pour la connexion."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Activé"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Activé"
        }
      }
    },
    "MFAChoice": {
      "title": "Authentification multi-facteurs"
    }
  },
  "ga-IE": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Gníomhachtaithe"
        }
      },
      "label": "SMS",
      "description": {
        "label": "Fáigh cód dearbhaithe trí SMS"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Gníomhachtaithe"
        }
      },
      "description": {
        "label": "Faigh cód fíordheimhnithe trí app fíordheimhnithe"
      },
      "label": "Aip Authindála"
    },
    "explanationMFAChoice": {
      "label": "Slándáil a mhéadú le fíordheimhniú ilfhachtóirí agus tú ag logáil isteach."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Gníomhachtaithe"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Gníomhachtaithe"
        }
      }
    },
    "MFAChoice": {
      "title": "Fíordheimhniú ilfhachtóirí"
    }
  },
  "he-IL": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "מופעל"
        }
      },
      "label": "סמס",
      "description": {
        "label": "קבל קוד אימות דרך SMS"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "מופעל"
        }
      },
      "description": {
        "label": "קבל קוד אימות באמצעות אפליקציית אימות"
      },
      "label": "אפליקציית אימות"
    },
    "explanationMFAChoice": {
      "label": "הגדל את האבטחה באמצעות אימות רב-גורמי בעת כניסה."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "מופעל"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "מופעל"
        }
      }
    },
    "MFAChoice": {
      "title": "אימות רב גורמים"
    }
  },
  "hi-IN": {
    "SMSMFA": {
      "feedforward": {
        "success": {}
      },
      "description": {
        "label": "एसएमएस के माध्यम से प्रमाणीकरण कोड प्राप्त करें"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {}
      },
      "description": {
        "label": "प्रामाणिकरण ऐप के माध्यम से प्रामाणिकरण कोड प्राप्त करें"
      },
      "label": "प्रमाणीकरण ऐप"
    },
    "explanationMFAChoice": {},
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "सक्रिय"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "सक्रिय"
        }
      }
    },
    "MFAChoice": {}
  },
  "hr-HR": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Aktiviran"
        }
      },
      "label": "SMS",
      "description": {
        "label": "Primite autentifikacijski kod putem SMS-a"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Aktiviran"
        }
      },
      "description": {
        "label": "Primite autentifikacijski kod putem autentifikacijske aplikacije"
      },
      "label": "Aplikacija za autentifikaciju"
    },
    "explanationMFAChoice": {
      "label": "Povećajte sigurnost pomoću višefaktorske provjere autentičnosti prilikom prijave."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Aktiviran"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Aktiviran"
        }
      }
    },
    "MFAChoice": {
      "title": "Višefaktorska autentifikacija"
    }
  },
  "hu-HU": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Aktivált"
        }
      },
      "label": "SMS",
      "description": {
        "label": "Fogadja el az ellenőrző kódot SMS-ben"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Aktivált"
        }
      },
      "description": {
        "label": "Szerezze be az hitelesítési kódot az hitelesítési alkalmazáson keresztül"
      },
      "label": "Hitelesítő alkalmazás"
    },
    "explanationMFAChoice": {
      "label": "Növelje a biztonságot többfaktoros hitelesítéssel a bejelentkezéskor."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Aktivált"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Aktivált"
        }
      }
    },
    "MFAChoice": {
      "title": "Többfaktoros hitelesítés"
    }
  },
  "it-CH": {
    "SMSMFA": {
      "feedforward": {
        "success": {}
      },
      "description": {
        "label": "Ricevi il codice di autenticazione tramite SMS"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {}
      },
      "description": {
        "label": "Ricevi il codice di autenticazione tramite l'app di autenticazione"
      },
      "label": "App di autenticazione"
    },
    "explanationMFAChoice": {},
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Attivato"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Attivato"
        }
      }
    },
    "MFAChoice": {}
  },
  "it-IT": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Attivato"
        }
      },
      "label": "SMS",
      "description": {
        "label": "Ricevi il codice di autenticazione tramite SMS"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Attivato"
        }
      },
      "description": {
        "label": "Ricevi il codice di autenticazione tramite l'app di autenticazione"
      },
      "label": "App di autenticazione"
    },
    "explanationMFAChoice": {
      "label": "Aumenta la sicurezza con l'autenticazione a più fattori durante l'accesso."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Attivato"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Attivato"
        }
      }
    },
    "MFAChoice": {
      "title": "Autenticazione a più fattori"
    }
  },
  "ja-JP": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "アクティブ化"
        }
      },
      "label": "SMS",
      "description": {
        "label": "SMSで認証コードを受け取る"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "アクティブ化"
        }
      },
      "description": {
        "label": "認証アプリを経由して認証コードを受け取る"
      },
      "label": "認証アプリ"
    },
    "explanationMFAChoice": {
      "label": "ログイン時の多要素認証でセキュリティを強化。"
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "アクティブ化"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "アクティブ化"
        }
      }
    },
    "MFAChoice": {
      "title": "多要素認証"
    }
  },
  "ko-KR": {
    "SMSMFA": {
      "feedforward": {
        "success": {}
      },
      "description": {
        "label": "SMS를 통해 인증 코드 받기"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {}
      },
      "description": {
        "label": "인증 앱을 통해 인증 코드를 받으세요"
      },
      "label": "인증 앱"
    },
    "explanationMFAChoice": {},
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "활성화됨"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "활성화됨"
        }
      }
    },
    "MFAChoice": {}
  },
  "lt-LT": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Suaktyvinta"
        }
      },
      "label": "Trumpoji žinutė",
      "description": {
        "label": "Gauti autentifikacijos kodą per SMS"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Suaktyvinta"
        }
      },
      "description": {
        "label": "Gaukite autentifikacijos kodą per autentifikacijos programą"
      },
      "label": "Autentifikavimo programa"
    },
    "explanationMFAChoice": {
      "label": "Padidinkite saugumą naudodami dviejų veiksnių autentifikavimą, kai prisijungiate."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Suaktyvinta"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Suaktyvinta"
        }
      }
    },
    "MFAChoice": {
      "title": "Dviejų veiksnių autentifikavimas"
    }
  },
  "lv-LV": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Aktivizēts"
        }
      },
      "label": "Īsziņa",
      "description": {
        "label": "Saņemt autentifikācijas kodu caur SMS"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Aktivizēts"
        }
      },
      "description": {
        "label": "Saņemiet autentifikācijas kodu caur autentifikācijas lietotni"
      },
      "label": "Autentifikācijas lietotne"
    },
    "explanationMFAChoice": {
      "label": "Palieliniet drošību, piesakoties, izmantojot daudzfaktoru autentifikāciju."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Aktivizēts"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Aktivizēts"
        }
      }
    },
    "MFAChoice": {
      "title": "Daudzfaktoru autentifikācija"
    }
  },
  "mt-MT": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Attivat"
        }
      },
      "label": "SMS",
      "description": {
        "label": "Irċievi kod ta' awtentikazzjoni permezz ta 'SMS"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Attivat"
        }
      },
      "description": {
        "label": "Irċievi kodiċi ta' awtentikazzjoni permezz ta' applikazzjoni ta' awtentikazzjoni"
      },
      "label": "App ta' awtentikazzjoni"
    },
    "explanationMFAChoice": {
      "label": "Żid is-sigurtà b'awtentikazzjoni b'ħafna fatturi meta tidħol."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Attivat"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Attivat"
        }
      }
    },
    "MFAChoice": {
      "title": "Awtentikazzjoni b'Ħafna Fatturi"
    }
  },
  "nb-NO": {
    "SMSMFA": {
      "feedforward": {
        "success": {}
      },
      "description": {
        "label": "Motta autentiseringskode via SMS"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {}
      },
      "description": {
        "label": "Motta autentiseringskode via autentiseringsapp"
      },
      "label": "Autentiseringsapp"
    },
    "explanationMFAChoice": {},
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Aktivert"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Aktivert"
        }
      }
    },
    "MFAChoice": {}
  },
  "nl-BE": {
    "SMSMFA": {
      "feedforward": {
        "success": {}
      },
      "description": {
        "label": "Ontvang authenticatiecode via SMS"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {}
      },
      "description": {
        "label": "Ontvang authenticatiecode via authenticatie-app"
      },
      "label": "Authenticatie-app"
    },
    "explanationMFAChoice": {},
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Geactiveerd"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Geactiveerd"
        }
      }
    },
    "MFAChoice": {}
  },
  "nl-NL": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Geactiveerd"
        }
      },
      "label": "SMS",
      "description": {
        "label": "Ontvang authenticatiecode via SMS"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Geactiveerd"
        }
      },
      "description": {
        "label": "Ontvang authenticatiecode via authenticatie-app"
      },
      "label": "Authenticatie-app"
    },
    "explanationMFAChoice": {
      "label": "Verhoog de veiligheid met multifactorauthenticatie bij het aanmelden."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Geactiveerd"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Geactiveerd"
        }
      }
    },
    "MFAChoice": {
      "title": "Multi-Factor Authenticatie"
    }
  },
  "pl-PL": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Aktywowano"
        }
      },
      "label": "SMS",
      "description": {
        "label": "Otrzymaj kod uwierzytelniający przez SMS"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Aktywowano"
        }
      },
      "description": {
        "label": "Odbierz kod uwierzytelniania za pośrednictwem aplikacji uwierzytelniania"
      },
      "label": "Aplikacja do uwierzytelniania"
    },
    "explanationMFAChoice": {
      "label": "Zwiększ bezpieczeństwo dzięki uwierzytelnianiu wieloskładnikowemu podczas logowania."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Aktywowano"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Aktywowano"
        }
      }
    },
    "MFAChoice": {
      "title": "Uwierzytelnianie wieloskładnikowe"
    }
  },
  "pt-PT": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Ativado"
        }
      },
      "label": "SMS",
      "description": {
        "label": "Receba o código de autenticação por SMS"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Ativado"
        }
      },
      "description": {
        "label": "Receba código de autenticação via aplicativo de autenticação"
      },
      "label": "Aplicativo de autenticação"
    },
    "explanationMFAChoice": {
      "label": "Aumente a segurança com a autenticação multifator ao efetuar o início de sessão."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Ativado"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Ativado"
        }
      }
    },
    "MFAChoice": {
      "title": "Autenticação multifator"
    }
  },
  "ro-RO": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Activat"
        }
      },
      "label": "SMS",
      "description": {
        "label": "Primiți codul de autentificare prin SMS"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Activat"
        }
      },
      "description": {
        "label": "Primiți cod de autentificare prin aplicația de autentificare"
      },
      "label": "Aplicație de autentificare"
    },
    "explanationMFAChoice": {
      "label": "Îmbunătățiti securitatea prin autentificarea cu mai mulți factori la conectare."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Activat"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Activat"
        }
      }
    },
    "MFAChoice": {
      "title": "Autentificare cu mai mulți factori"
    }
  },
  "ru-RU": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Активировано"
        }
      },
      "label": "SMS",
      "description": {
        "label": "Получить код аутентификации по SMS"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Активировано"
        }
      },
      "description": {
        "label": "Получите код аутентификации через приложение для аутентификации"
      },
      "label": "Приложение для аутентификации"
    },
    "explanationMFAChoice": {
      "label": "Повысьте безопасность с помощью многофакторной аутентификации при входе в систему."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Активировано"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Активировано"
        }
      }
    },
    "MFAChoice": {
      "title": "Многофакторная аутентификация"
    }
  },
  "sk-SK": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Aktivované"
        }
      },
      "label": "SMS",
      "description": {
        "label": "Prijmite overovací kód cez SMS"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Aktivované"
        }
      },
      "description": {
        "label": "Získajte overovací kód cez overovaciu aplikáciu"
      },
      "label": "Autentifikačná aplikácia"
    },
    "explanationMFAChoice": {
      "label": "Zvýšte zabezpečenie pomocou viacfaktorového overenia pri prihlasovaní."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Aktivované"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Aktivované"
        }
      }
    },
    "MFAChoice": {
      "title": "Viacfaktorové overenie"
    }
  },
  "sl-SI": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Aktivirano"
        }
      },
      "label": "SMS",
      "description": {
        "label": "Prejmite avtentikacijsko kodo prek SMS"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Aktivirano"
        }
      },
      "description": {
        "label": "Prejmite avtentikacijsko kodo preko avtentikacijske aplikacije"
      },
      "label": "Aplikacija za avtentikacijo"
    },
    "explanationMFAChoice": {
      "label": "Izboljšajte varnost z večkratnim preverjanjem pristnosti ob prijavi."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Aktivirano"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Aktivirano"
        }
      }
    },
    "MFAChoice": {
      "title": "Večkratno preverjanje pristnosti"
    }
  },
  "sv-SE": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Aktiverad"
        }
      },
      "label": "SMS",
      "description": {
        "label": "Ta emot autentiseringskod via SMS"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Aktiverad"
        }
      },
      "description": {
        "label": "Ta emot autentiseringskod via autentiseringsapp"
      },
      "label": "Autentiseringsapp"
    },
    "explanationMFAChoice": {
      "label": "Öka säkerheten med autentisering med flera faktorer när du loggar in."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Aktiverad"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Aktiverad"
        }
      }
    },
    "MFAChoice": {
      "title": "Multifaktorautentisering"
    }
  },
  "th-TH": {
    "SMSMFA": {
      "feedforward": {
        "success": {}
      },
      "description": {
        "label": "รับรหัสยืนยันผ่าน SMS"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {}
      },
      "description": {
        "label": "รับรหัสการยืนยันตัวตนผ่านแอปการยืนยันตัวตน"
      },
      "label": "แอปพลิเคชันการรับรองความถูกต้อง"
    },
    "explanationMFAChoice": {},
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "เปิดใช้งานแล้ว"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "เปิดใช้งานแล้ว"
        }
      }
    },
    "MFAChoice": {}
  },
  "tr-CY": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Etkinleştirildi"
        }
      },
      "label": "SMS",
      "description": {
        "label": "SMS ile doğrulama kodu alın"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Etkinleştirildi"
        }
      },
      "description": {
        "label": "Doğrulama uygulaması aracılığıyla doğrulama kodunu alın"
      },
      "label": "Kimlik Doğrulama Uygulaması"
    },
    "explanationMFAChoice": {
      "label": "Oturum açarken çok faktörlü kimlik doğrulama ile güvenliği artırın."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Etkinleştirildi"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Etkinleştirildi"
        }
      }
    },
    "MFAChoice": {
      "title": "Çok faktörlü kimlik doğrulama"
    }
  },
  "tr-TR": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Etkinleştirildi"
        }
      },
      "label": "SMS",
      "description": {
        "label": "SMS ile doğrulama kodu alın"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Etkinleştirildi"
        }
      },
      "description": {
        "label": "Doğrulama uygulaması aracılığıyla doğrulama kodunu alın"
      },
      "label": "Kimlik Doğrulama Uygulaması"
    },
    "explanationMFAChoice": {
      "label": "Oturum açarken çok faktörlü kimlik doğrulama ile güvenliği artırın."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Etkinleştirildi"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Etkinleştirildi"
        }
      }
    },
    "MFAChoice": {
      "title": "Çok faktörlü kimlik doğrulama"
    }
  },
  "uk-UA": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "Активовано"
        }
      },
      "label": "SMS",
      "description": {
        "label": "Отримайте код автентифікації через SMS"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "Активовано"
        }
      },
      "description": {
        "label": "Отримайте код автентифікації через додаток для автентифікації"
      },
      "label": "Додаток для автентифікації"
    },
    "explanationMFAChoice": {
      "label": "Erhöhe die Sicherheit durch Multi-Faktor-Authentifizierung beim Login."
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Активовано"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Активовано"
        }
      }
    },
    "MFAChoice": {
      "title": "Багатофакторна автентифікація"
    }
  },
  "vi-VN": {
    "SMSMFA": {
      "feedforward": {
        "success": {}
      },
      "description": {
        "label": "Nhận mã xác thực qua SMS"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {}
      },
      "description": {
        "label": "Nhận mã xác thực qua ứng dụng xác thực"
      },
      "label": "Ứng dụng Xác thực"
    },
    "explanationMFAChoice": {},
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "Đã kích hoạt"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "Đã kích hoạt"
        }
      }
    },
    "MFAChoice": {}
  },
  "zh-CN": {
    "SMSMFA": {
      "feedforward": {
        "success": {
          "message": "已激活"
        }
      },
      "label": "短信",
      "description": {
        "label": "通过短信接收认证码"
      }
    },
    "authenticatorMFA": {
      "feedforward": {
        "success": {
          "message": "已激活"
        }
      },
      "description": {
        "label": "通过认证应用获取认证代码"
      },
      "label": "认证应用"
    },
    "explanationMFAChoice": {
      "label": "登录时通过多重身份验证提高安全性。"
    },
    "feedforward": {
      "SMSMFA": {
        "success": {
          "message": "已激活"
        }
      },
      "authenticatorMFA": {
        "success": {
          "message": "已激活"
        }
      }
    },
    "MFAChoice": {
      "title": "多重身份验证"
    }
  }
}
</i18n>
